import { currencyFractionDigits } from '../../shared/utils/commercial-money';
import { AccountingDirectoryControlsComponent } from '../shared/accounting-directory-controls.component';
import * as XLSX from 'xlsx';
import { Component, OnInit, inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { AccountsService } from "../accounts.service";
import { SessionStorageService } from "../../services/session-storage.service";
import { AccountReceivables } from "./receivables.model";
import { AccountReceivablesLine } from "./receivables-line.model";
import { Invoice } from '../invoice/invoice.model';
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { Router } from "@angular/router";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { PanelModule } from "primeng/panel";
import { ImageModule } from "primeng/image";
import { ChipModule } from "primeng/chip";
import { TagModule } from "primeng/tag";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { DrawerModule } from "primeng/drawer";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../company-scope-selector/company-scope-selector.component';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../../shared/crm-client-contact-picker/crm-party-context.service';
import { LocaleService } from '../../i18n/locale.service';
import { distinctSourceReferences } from '../source-references';
import { OrdersService } from '../../order/order.service';


@Component({
    selector: 'app-receivables',
    templateUrl: './receivables.component.html',
    styleUrls: ['../finance-list.shared.css', './receivables.component.css'],
    providers: [
        MessageService,
        AccountsService,
        SessionStorageService
    ],
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    TableModule,
    MultiSelectModule,
    DatePickerModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    TooltipModule,
    ToastModule,
    ProgressBarModule,
    PanelModule,
    ImageModule,
    ChipModule,
    TagModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    DrawerModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent,
    AccountingDirectoryControlsComponent,
        CompanyScopeSelectorComponent
]
})
export class ReceivablesComponent implements OnInit {
    readonly pendingStatuses = ['NEW', 'DRAFT', 'READY', 'APPROVAL_REQUIRED', 'APPROVED', 'ON_HOLD', 'DISPUTED'];
    searchText = '';
    directoryMode: 'LIST' | 'CRM' | 'PARTY_TYPE' = 'LIST';
    directoryFilters: Record<string, string | number | boolean | number[] | string[] | null | undefined> = {};
    private listRequestId = 0;

    private readonly localeService = inject(LocaleService);
    private readonly ordersService = inject(OrdersService);
    invoiceSelectionBlocker = '';
    checkingInvoiceSelection = false;
    private invoiceSelectionRequest = 0;

    isLoading = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    // Data properties for the unified table
    allReceivables: AccountReceivables[] = [];
    selectedReceivables: AccountReceivables[] = [];

    get canGenerateSelectedInvoice(): boolean {
        return this.selectedReceivables.length > 0 && !this.checkingInvoiceSelection && !this.invoiceSelectionBlocker;
    }

    onReceivableSelectionChange(selection: AccountReceivables[]): void {
        this.selectedReceivables = selection;
        const request = ++this.invoiceSelectionRequest;
        this.invoiceSelectionBlocker = '';
        this.checkingInvoiceSelection = false;
        if (this.selectedReceivables.some(item => item.invoiceId || item.invoiceNumber
            || ['INVOICED', 'DRAFT'].includes(item.status))) {
            this.invoiceSelectionBlocker = 'A selected receivable is already linked to an invoice.';
            return;
        }
        if (this.selectedReceivables.some(item => item.sourceResourceType?.toUpperCase() === 'SALES_ORDER'
            && !item.sourceResourceId)) {
            this.invoiceSelectionBlocker = 'A sales-order receivable has no source order.';
            return;
        }
        const orderItems = this.selectedReceivables.filter(item =>
            item.sourceResourceType?.toUpperCase() === 'SALES_ORDER' && item.sourceResourceId);
        if (!orderItems.length) return;
        this.checkingInvoiceSelection = true;
        forkJoin(orderItems.map(item => this.ordersService.getInvoiceReadiness(item.sourceResourceId!))).subscribe({
            next: results => {
                if (request !== this.invoiceSelectionRequest) return;
                for (let index = 0; index < results.length; index++) {
                    const readiness = results[index];
                    const item = orderItems[index];
                    if (readiness.receivableId !== item.receivableId) {
                        this.invoiceSelectionBlocker = 'This sales order has missing or duplicate receivables.';
                        break;
                    }
                    if (readiness.invoiceId) {
                        this.invoiceSelectionBlocker = `Sales order ${readiness.orderNumber} already has an invoice.`;
                        break;
                    }
                    const orderBlocker = readiness.blockingReasons.find(reason =>
                        !reason.toLowerCase().includes('cannot be refreshed')
                        && !reason.toLowerCase().includes('already linked to an invoice'));
                    if (orderBlocker) {
                        this.invoiceSelectionBlocker = orderBlocker;
                        break;
                    }
                    if (readiness.invoicePolicy === 'DELIVERED_QUANTITY' && readiness.lines.some(line =>
                        line.deliveredQuantity - line.returnedQuantity + 0.000001
                        < line.orderedQuantity - line.cancelledQuantity)) {
                        this.invoiceSelectionBlocker = `Sales order ${readiness.orderNumber} must be fully delivered before invoicing.`;
                        break;
                    }
                }
                this.checkingInvoiceSelection = false;
            },
            error: () => {
                if (request !== this.invoiceSelectionRequest) return;
                this.invoiceSelectionBlocker = 'Unable to verify the selected sales order. Refresh and try again.';
                this.checkingInvoiceSelection = false;
            }
        });
    }
    totalRecords: number = 0;
    first: number = 0;
    rows: number = 20;

    // Filter properties
    clients: any[] = [];
    selectedClients: any[] = [];
    contacts: any[] = [];
    selectedContacts: any[] = [];
    dateRange: (Date | null)[] | null = null;
    selectedStatuses: string[] = this.pendingStatuses.slice();
    selectedSourceTypes: string[] = [];
    referenceFilter: string | null = null;
    sourceReferenceFilter: string | null = null;
    invoiceNumberFilter: string | null = null;

    // Summary Card properties
    totalPendingAmount: number = 0;
    totalInvoicedAmount: number = 0;
    reportCurrencyCode: string = 'USD'; // Default, will be updated based on company settings

    canViewReceivables: boolean = false;
    canGenerateInvoice: boolean = false;
    canEditReceivables: boolean = false;
    canExportReceivables: boolean = false;
    previewReceivable: AccountReceivables | null = null;
    isPreviewVisible = false;
    currentScope: CompanyScopeSelection | null = null;
    createdInvoiceDrafts: Invoice[] = [];

    get isConsolidatedReadOnly(): boolean {
        return !!this.currentScope?.readOnly;
    }

    constructor(private accountsService: AccountsService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private router: Router,
        private permissionService: PermissionService,
        public crmPartyContext: CrmPartyContextService) {
    }


    ngOnInit(): void {
        this.canViewReceivables = this.permissionService.hasPermission(app_permissions.accounts.invoice.view);
        this.canGenerateInvoice = this.permissionService.hasPermission(app_permissions.accounts.invoice.create);
        this.canEditReceivables = this.permissionService.hasPermission(app_permissions.accounts.invoice.edit);
        this.canExportReceivables = this.permissionService.hasPermission(app_permissions.accounts.invoice.view);
        this.breadcrumbItems = [
            { label: this.t('pages.receivables.breadcrumb.accounts') },
            { label: this.t('pages.receivables.breadcrumb.enterprise') },
            { label: this.t('pages.receivables.breadcrumb.receivables') }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        if (!this.canViewReceivables) {
            this.isLoading = false;
            return;
        }
        this.loadInitialData();
    }

    onScopeChange(scope: CompanyScopeSelection) {
        this.currentScope = scope;
        this.selectedReceivables = [];
        this.applyFilters();
    }

    loadInitialData() {
        if (!this.canViewReceivables) {
            this.isLoading = false;
            return;
        }
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.receivables.messages.companyNotFound') });
            this.isLoading = false;
            return;
        }

        this.crmPartyContext.loadClients(companyId, CRM_RECEIVABLE_ROLES).subscribe(clients => {
            this.clients = clients.map(client => ({ ...client, name: this.crmPartyContext.clientName(client) }));
        });

        // Initial data load for the table and summary is triggered by applyFilters
        this.applyFilters();
    }

    loadData(event: TableLazyLoadEvent) {
        this.isLoading = true;
        const requestId = ++this.listRequestId;
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;

        const sessionCompanyId = this.sessionStorageService.getCompany()?.id;
        const companyGroupId = this.currentScope?.companyGroupId;
        const companyId = companyGroupId ? undefined : (this.currentScope?.companyId ?? sessionCompanyId);
        if (!companyId && !companyGroupId) return;

        const clientIds = this.selectedClients.map(c => Number(c.id?.entityId ?? c.id)).filter(Boolean);
        const contactIds = this.selectedContacts.map(c => Number(c.id)).filter(Boolean);
        const startDate = this.dateRange?.[0] ? this.dateRange[0].toISOString() : undefined;
        const endDate = this.dateRange?.[1] ? this.dateRange[1].toISOString() : undefined;

        // NOTE: This assumes 'getAccountReceivables' service method is updated to handle these new filters.
        this.directoryFilters = { owningCompanyId: companyId, companyGroupId, clientIds, crmClientContactIds: contactIds, startDate, endDate, referenceNumber: this.referenceFilter, invoiceNumber: this.invoiceNumberFilter, sourceTypes: this.selectedSourceTypes, sourceReference: this.sourceReferenceFilter, searchText: this.searchText };

        this.accountsService.getAccountReceivables(
            this.first / this.rows, this.rows, companyId, clientIds, this.selectedStatuses,
            startDate, endDate, this.referenceFilter ?? undefined, this.invoiceNumberFilter ?? undefined,
            this.selectedSourceTypes, undefined, undefined, undefined, undefined, companyGroupId, contactIds,
            this.sourceReferenceFilter?.trim() || undefined, this.searchText
        ).subscribe({
            next: (dataWrapper) => {
                if (requestId !== this.listRequestId) return;
                this.allReceivables = dataWrapper.items;
                this.totalRecords = dataWrapper.count;
                if (this.allReceivables.length > 0) {
                    this.reportCurrencyCode = this.allReceivables[0].country.currencyCode;
                }
                this.isLoading = false;
            },
            error: (err) => {
                if (requestId !== this.listRequestId) return;
                this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.receivables.messages.couldNotLoad') });
                this.isLoading = false;
            }
        });

        // NOTE: This assumes 'getClientReceivablesSummary' is updated to handle filters for accurate summary cards.
        this.accountsService.getClientReceivablesSummary(companyGroupId ? undefined : companyId, clientIds, this.selectedStatuses, startDate, endDate, this.referenceFilter ?? undefined, this.invoiceNumberFilter ?? undefined, companyGroupId)
            .subscribe(summary => {
                if (requestId !== this.listRequestId) return;
                this.totalPendingAmount = summary.pendingAmount;
                this.totalInvoicedAmount = summary.invoicedAmount;
            });
    }

    onDirectoryModeChange(mode: 'LIST' | 'CRM' | 'PARTY_TYPE'): void {
        this.directoryMode = mode;
        this.selectedReceivables = [];
    }

    onDirectorySearch(value: string): void {
        this.searchText = value;
        this.selectedReceivables = [];
        this.applyFilters();
    }

    showPendingStatuses(): void {
        this.selectedStatuses = this.pendingStatuses.slice();
        this.applyFilters();
    }

    showAllStatuses(): void {
        this.selectedStatuses = [];
        this.applyFilters();
    }

    applyFilters() {
        // Reset to the first page when applying filters
        this.loadData({ first: 0, rows: this.rows });
    }

    clearFilters() {
        this.selectedClients = [];
        this.contacts = [];
        this.selectedContacts = [];
        this.dateRange = null;
        this.selectedStatuses = this.pendingStatuses.slice();
        this.selectedSourceTypes = [];
        this.referenceFilter = null;
        this.sourceReferenceFilter = null;
        this.invoiceNumberFilter = null;
        this.searchText = '';
        this.applyFilters();
    }

    get metrics(): PageMetric[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.receivables.metrics.readyAmount'), value: this.formatMoney(this.amountByStatus(['NEW', 'READY', 'APPROVED'])), hint: this.t('pages.receivables.metrics.readyHint'), tone: 'success' },
            { label: this.t('pages.receivables.metrics.selectedAmount'), value: this.formatMoney(this.getSelectedTotal()), hint: this.t('pages.receivables.metrics.selectedHint', { count: this.selectedReceivables.length }), tone: 'info' },
            { label: this.t('pages.receivables.metrics.onHoldAmount'), value: this.formatMoney(this.amountByStatus(['ON_HOLD'])), hint: this.t('pages.receivables.metrics.onHoldHint'), tone: 'warn' },
            { label: this.t('pages.receivables.metrics.disputedAmount'), value: this.formatMoney(this.amountByStatus(['DISPUTED'])), hint: this.t('pages.receivables.metrics.disputedHint'), tone: 'danger' },
            { label: this.t('pages.receivables.metrics.approvalRequired'), value: this.formatMoney(this.amountByStatus(['APPROVAL_REQUIRED'])), hint: this.t('pages.receivables.metrics.approvalHint'), tone: 'warn' }
        ];
    }

    get statusOptions(): { label: string; value: string }[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.receivables.status.new'), value: 'NEW' },
            { label: this.t('pages.receivables.status.ready'), value: 'READY' },
            { label: this.t('pages.receivables.status.draft'), value: 'DRAFT' },
            { label: this.t('pages.receivables.status.onHold'), value: 'ON_HOLD' },
            { label: this.t('pages.receivables.status.disputed'), value: 'DISPUTED' },
            { label: this.t('pages.receivables.status.approvalRequired'), value: 'APPROVAL_REQUIRED' },
            { label: this.t('pages.receivables.status.approved'), value: 'APPROVED' },
            { label: this.t('pages.receivables.status.invoiced'), value: 'INVOICED' },
            { label: this.t('pages.receivables.status.cancelled'), value: 'CANCELLED' },
            { label: this.t('pages.receivables.status.reversed'), value: 'REVERSED' }
        ];
    }

    get sourceTypeOptions(): { label: string; value: string }[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.receivables.sourceTypes.system'), value: 'SYSTEM' },
            { label: this.t('pages.receivables.sourceTypes.manual'), value: 'MANUAL' }
        ];
    }

    get savedFilterViews() {
        this.localeService.locale();
        return [
            { label: this.t('pages.receivables.savedViews.readyToInvoice'), statuses: ['NEW', 'READY', 'APPROVED'] },
            { label: this.t('pages.receivables.savedViews.needsReview'), statuses: ['APPROVAL_REQUIRED'] },
            { label: this.t('pages.receivables.savedViews.manualLines'), statuses: [], sourceTypes: ['MANUAL'] },
            { label: this.t('pages.receivables.savedViews.onHold'), statuses: ['ON_HOLD'] },
            { label: this.t('pages.receivables.savedViews.disputed'), statuses: ['DISPUTED'] },
            { label: this.t('pages.receivables.savedViews.drafted'), statuses: ['DRAFT'] },
            { label: this.t('pages.receivables.savedViews.alreadyInvoiced'), statuses: ['INVOICED'] },
        ];
    }

    get activeFilterCount(): number {
        return [
            this.selectedClients.length,
            this.selectedContacts.length,
            this.dateRange?.some(Boolean) ? 1 : 0,
            this.selectedStatuses.length,
            this.selectedSourceTypes.length,
            this.referenceFilter ? 1 : 0,
            this.sourceReferenceFilter?.trim() ? 1 : 0,
            this.invoiceNumberFilter ? 1 : 0
        ].reduce((total, value) => total + Number(value), 0);
    }

    get resultText(): string {
        this.localeService.locale();
        return this.t('pages.receivables.results.receivables', { count: this.totalRecords });
    }

    get currentPageReportTemplate(): string {
        this.localeService.locale();
        return this.t('pages.receivables.table.pageReport');
    }

    onSelectedClientsChange(): void {
        this.selectedContacts = [];
        this.contacts = [];
        const companyId = this.sessionStorageService.getCompany()?.id;
        const clientIds = this.selectedClients.map(client => Number(client.id)).filter(Boolean);
        if (!clientIds.length) return;
        forkJoin(clientIds.map(clientId => this.crmPartyContext.loadContacts(clientId, companyId))).subscribe(contactGroups => {
            const seen = new Set<number>();
            this.contacts = contactGroups
                .flat()
                .filter(contact => {
                    if (!contact.id || seen.has(contact.id)) return false;
                    seen.add(contact.id);
                    return true;
                })
                .map(contact => ({ ...contact, displayName: this.crmPartyContext.contactName(contact) }));
        });
    }

    getSelectedTotal(): number {
        return this.selectedReceivables.reduce((total, receivable) => total + (receivable.totalAmount || 0), 0);
    }

    get expectedInvoiceDraftCount(): number {
        return new Set(this.selectedReceivables.map(receivable => this.draftGroupKey(receivable))).size;
    }

    get generateInvoiceLabel(): string {
        const count = this.expectedInvoiceDraftCount;
        return count ? `Create ${count} invoice draft${count === 1 ? '' : 's'}` : 'Create invoice draft';
    }

    amountByStatus(statuses: string[]): number {
        return this.allReceivables
            .filter(receivable => statuses.includes(receivable.status || ''))
            .reduce((total, receivable) => total + (receivable.totalAmount || 0), 0);
    }

    formatMoney(amount: number | undefined, currencyCode: string = this.reportCurrencyCode): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: currencyFractionDigits(currencyCode || 'SAR'), maximumFractionDigits: currencyFractionDigits(currencyCode || 'SAR') })}`;
    }

    displayDocumentReference(value?: string): string {
        if (!value) return this.t('pages.receivables.table.noReference');
        if (/^AR\/\d{4}\/\d{2}\/\d{8}$/.test(value)) return value;
        const operationalNumber = value.match(/(?:SHP|DLR|SO|PO|CLR)\/\d{4}\/\d{2}\/\d+/)?.[0];
        return operationalNumber || (value.length > 44 ? `${value.slice(0, 41)}…` : value);
    }

    statusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'INVOICED') return 'success';
        if (status === 'NEW' || status === 'READY' || status === 'DRAFT') return 'info';
        if (status === 'ON_HOLD' || status === 'APPROVAL_REQUIRED') return 'warn';
        if (status === 'DISPUTED' || status === 'CANCELLED' || status === 'REVERSED') return 'danger';
        return 'secondary';
    }

    statusLabel(status: string | undefined): string {
        this.localeService.locale();
        switch (status) {
            case 'NEW': return this.t('pages.receivables.status.new');
            case 'READY': return this.t('pages.receivables.status.ready');
            case 'DRAFT': return this.t('pages.receivables.status.draft');
            case 'ON_HOLD': return this.t('pages.receivables.status.onHold');
            case 'DISPUTED': return this.t('pages.receivables.status.disputed');
            case 'APPROVAL_REQUIRED': return this.t('pages.receivables.status.approvalRequired');
            case 'APPROVED': return this.t('pages.receivables.status.approved');
            case 'INVOICED': return this.t('pages.receivables.status.invoiced');
            case 'CANCELLED': return this.t('pages.receivables.status.cancelled');
            case 'REVERSED': return this.t('pages.receivables.status.reversed');
            default: return status?.trim() || this.t('pages.receivables.status.unknown');
        }
    }

    applySavedFilter(view: { statuses: string[]; sourceTypes?: string[] }) {
        this.selectedStatuses = [...view.statuses];
        this.selectedSourceTypes = [...(view.sourceTypes ?? [])];
        this.applyFilters();
    }

    openPreview(receivable: AccountReceivables) {
        this.previewReceivable = receivable;
        this.isPreviewVisible = true;
    }

    viewReceivable(receivable: AccountReceivables): void {
        if (receivable.receivableId) {
            this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced/view', receivable.receivableId]);
        }
    }

    createManualReceivable() {
        this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced/manual']);
    }

    canEditReceivable(receivable: AccountReceivables): boolean {
        return !this.isConsolidatedReadOnly
            && this.canEditReceivables
            && !!receivable.receivableId
            && !receivable.invoiceId && !receivable.invoiceNumber
            && !['INVOICED', 'CANCELLED', 'REVERSED'].includes(receivable.status || '');
    }

    editReceivable(receivable: AccountReceivables) {
        if (!this.canEditReceivable(receivable)) return;
        this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced/manual', receivable.receivableId]);
    }

    headerSourceReferences(receivable: AccountReceivables): string[] {
        return distinctSourceReferences([receivable.sourceReference, receivable.sourceResourceNumber]);
    }

    lineSourceReferences(receivable: AccountReceivables): string[] {
        return distinctSourceReferences(receivable.accountReceivablesLines?.map(line => line.receivableLineRefNumber) ?? []);
    }

    exportToExcel() {
        if (!this.canExportReceivables) {
            return;
        }
        if (this.allReceivables.length === 0) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.noDataSummary'), detail: this.t('pages.receivables.messages.noDataDetail') });
            return;
        }
        const worksheet = XLSX.utils.json_to_sheet(this.allReceivables.map(r => ({
            Client: this.crmPartyContext.displayName(r),
            ID: r.receivableId,
            Type: r.receivableTxnType,
            'Ref#': r.receivableReferenceNumber,
            'Header Source References': this.headerSourceReferences(r).join(', '),
            'Line Source References': this.lineSourceReferences(r).join(', '),
            Contact: this.crmPartyContext.contactDisplayName(r),
            Date: r.createdAt,
            Amount: r.totalAmount,
            Currency: r.country.currencyCode,
            'Invoice#': r.invoiceNumber,
            'Invoice Date': r.invoicingDate,
            Status: r.status
        })));
        const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, "ReceivablesExport");
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName + '_' + new Date().getTime() + '.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    viewDetails(receivable: AccountReceivables) {
        if (receivable.sourceType === 'MANUAL' || String(receivable.receivableTxnType) === 'MANUAL') {
            this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced/manual', receivable.receivableId]);
            return;
        }
        switch (receivable.receivableTxnType) {
            case 'SHIPMENT':
                this.router.navigate(['/shipment/view', receivable.accountReceivablesLines[0].headerReferenceId]);
                break;
            case 'CLEARANCE':
                this.router.navigate(['/clearance/view', receivable.accountReceivablesLines[0].headerReferenceId]);
                break;
            case 'ORDER':
                this.router.navigate(['/order/sales-order/view', receivable.accountReceivablesLines[0].headerReferenceId]);
                break;
            default:
                this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.notSupportedSummary'), detail: this.t('pages.receivables.messages.notSupportedDetail') });
        }
    }

    editReceivableLine(receivableLine: AccountReceivablesLine) {

    }

    generateInvoice() {
        if (this.checkingInvoiceSelection || this.invoiceSelectionBlocker) {
            this.messageService.add({ severity: 'warn', summary: this.t('common.error'),
                detail: this.invoiceSelectionBlocker || 'Checking sales order readiness.' });
            return;
        }
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.readOnlyScopeSummary'), detail: this.t('pages.receivables.messages.generateInvoicesReadOnly') });
            return;
        }
        if (!this.canGenerateInvoice) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.permissionSummary'), detail: this.t('pages.receivables.messages.noGeneratePermission') });
            return;
        }
        if (!this.selectedReceivables || this.selectedReceivables.length === 0) {
            this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.receivables.messages.selectAtLeastOneReceivable') });
            return;
        }
        if (this.selectedReceivables.some(value => ['INVOICED', 'DRAFT'].includes(value.status))) {
            this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.receivables.messages.alreadyDraftedOrInvoiced') });
            return;
        }
        this.isLoading = true;
        this.accountsService.generateInvoiceFromAccountReceivables(this.selectedReceivables).subscribe({
            next: (invoices) => {
                this.createdInvoiceDrafts = invoices || [];
                const draftNumbers = this.createdInvoiceDrafts.map(draft => draft.invoiceNumber).filter(Boolean).join(', ');
                this.messageService.add({ severity: 'success', summary: this.t('common.success'), detail: this.createdInvoiceDrafts.length === 1
                    ? `Draft ${draftNumbers} created.`
                    : `${this.createdInvoiceDrafts.length} invoice drafts created${draftNumbers ? `: ${draftNumbers}` : ''}.` });
                this.selectedReceivables = []; // Clear selection
                this.applyFilters(); // Refresh data to show updated statuses
                if (this.createdInvoiceDrafts.length === 1 && this.createdInvoiceDrafts[0].invoiceId) {
                    this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', this.createdInvoiceDrafts[0].invoiceId]);
                }
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: this.t('pages.receivables.messages.invoiceErrorSummary'), detail: this.t('pages.receivables.messages.invoiceErrorPrefix') + (err.error?.message || this.t('pages.receivables.messages.unknownError')) });
                this.isLoading = false;
            }
        });
    }

    openCreatedInvoice(invoice: Invoice): void {
        if (invoice.invoiceId) this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', invoice.invoiceId]);
    }

    private draftGroupKey(receivable: AccountReceivables): string {
        const party = receivable.mainParty?.id;
        return [receivable.owningCompany?.id || this.sessionStorageService.getCompany()?.id || '', party?.entityType || '', party?.entityId || '', (receivable as any).currencyCode || receivable.country?.currencyCode || 'SAR'].join('|');
    }

    holdSelected() {
        this.runBulkReceivableAction('ON_HOLD', this.t('pages.receivables.messages.heldSelected'), receivable => ({
            ...receivable,
            status: 'ON_HOLD',
            holdReason: receivable.holdReason || this.t('pages.receivables.messages.heldFromWorkbench')
        }));
    }

    releaseSelected() {
        this.runBulkReceivableAction('READY', this.t('pages.receivables.messages.releasedSelected'), receivable => ({
            ...receivable,
            status: 'READY',
            holdReason: undefined,
            disputeReason: undefined,
            reviewReason: receivable.reviewReason
        }));
    }

    disputeSelected() {
        this.runBulkReceivableAction('DISPUTED', this.t('pages.receivables.messages.disputedSelected'), receivable => ({
            ...receivable,
            status: 'DISPUTED',
            disputeReason: receivable.disputeReason || this.t('pages.receivables.messages.disputedFromWorkbench')
        }));
    }

    submitSelectedForApproval() {
        this.runBulkReceivableAction('APPROVAL_REQUIRED', this.t('pages.receivables.messages.submittedForApproval'), receivable => ({
            ...receivable,
            status: 'APPROVAL_REQUIRED',
            reviewReason: receivable.reviewReason || this.t('pages.receivables.messages.submittedFromWorkbench')
        }));
    }

    private runBulkReceivableAction(
        targetStatus: string,
        successDetail: string,
        mapper: (receivable: AccountReceivables) => AccountReceivables
    ) {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.readOnlyScopeSummary'), detail: this.t('pages.receivables.messages.updateReceivablesReadOnly') });
            return;
        }
        if (!this.canEditReceivables) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.permissionSummary'), detail: this.t('pages.receivables.messages.noUpdatePermission') });
            return;
        }
        const selected = this.selectedReceivables.filter(receivable => !!receivable.receivableId && !['INVOICED', 'CANCELLED', 'REVERSED'].includes(receivable.status || ''));
        if (!selected.length) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.receivables.messages.noEligibleRowsSummary'), detail: this.t('pages.receivables.messages.noEligibleRowsDetail') });
            return;
        }
        this.isLoading = true;
		forkJoin(selected.map(receivable => {
			const changed = mapper(receivable);
			return this.accountsService.updateAccountReceivableReadiness(receivable.receivableId!, {
				rowVersion: receivable.rowVersion,
				status: changed.status,
				holdReason: changed.holdReason,
				disputeReason: changed.disputeReason,
				reviewReason: changed.reviewReason
			});
		})).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: this.statusLabel(targetStatus), detail: successDetail });
                this.selectedReceivables = [];
                this.applyFilters();
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: this.t('pages.receivables.messages.bulkUpdateFailedSummary'), detail: err?.error?.message || this.t('pages.receivables.messages.bulkUpdateFailedDetail') });
                this.isLoading = false;
            }
        });
    }

    t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
        if (typeof fallbackOrParams === 'string') {
            return this.localeService.translateIfPresent(key, fallbackOrParams, params);
        }
        return this.localeService.translate(key, fallbackOrParams);
    }
}
